import { useState, useMemo } from 'react'
import { Head, usePage, Link } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Clock, Award, Layers, Search, ShieldCheck,
  TrendingUp, ArrowRight, Zap, BookOpen, CheckCircle2,
  Filter
} from 'lucide-react'
import type { PageProps } from '@/types'

interface MockTestItem {
  id: number
  title: string
  description?: string
  exam_name: string
  exam_code?: string
  duration_minutes: number
  question_count?: number
  total_marks?: number
  positive_marks: number
  negative_marking: number
  difficulty: string
  test_type: string
  is_free: boolean
  is_sectional: boolean
}

interface PublicMockTestsProps {
  tests: MockTestItem[]
  seo: {
    title: string
    description: string
    keywords: string
  }
}

export default function PublicMockTests({ tests = [], seo }: PublicMockTestsProps) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedExam, setSelectedExam] = useState<string>('all')
  const [selectedType, setSelectedType] = useState<string>('all')

  // Extract unique exams for quick filtering tabs
  const examOptions = useMemo(() => {
    const exams = Array.from(new Set(tests.map(t => t.exam_name).filter(Boolean)))
    return exams.sort()
  }, [tests])

  const filteredTests = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    return tests.filter(test => {
      const matchesSearch = !q ||
        test.title.toLowerCase().includes(q) ||
        test.exam_name.toLowerCase().includes(q) ||
        (test.description && test.description.toLowerCase().includes(q))

      const matchesExam = selectedExam === 'all' || test.exam_name === selectedExam

      const matchesType =
        selectedType === 'all' ||
        (selectedType === 'free' && test.is_free) ||
        (selectedType === 'full_mock' && test.test_type === 'full_mock') ||
        (selectedType === 'sectional' && (test.is_sectional || test.test_type === 'sectional')) ||
        (selectedType === 'chapter' && test.test_type === 'chapter_test')

      return matchesSearch && matchesExam && matchesType
    })
  }, [tests, searchQuery, selectedExam, selectedType])

  // Count helper
  const freeTestsCount = useMemo(() => tests.filter(t => t.is_free).length, [tests])
  const fullMockCount = useMemo(() => tests.filter(t => t.test_type === 'full_mock').length, [tests])

  // JSON-LD Structured Data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free Online Mock Tests for Indian Competitive Exams',
    description: seo.description,
    url: 'https://prepkiya.com/mock-tests',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://prepkiya.com'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Mock Tests',
          item: 'https://prepkiya.com/mock-tests'
        }
      ]
    }
  }

  return (
    <>
      <Head title={seo.title}>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href="https://prepkiya.com/mock-tests" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content="https://prepkiya.com/mock-tests" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-[#fafbfc] text-slate-900 antialiased selection:bg-brand-gold/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 flex flex-col">
          {/* Top Hero Section */}
          <section className="w-full pt-12 pb-10 sm:pt-16 sm:pb-14 bg-white border-b border-slate-200/80">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center space-y-4">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-xs font-bold tracking-wide uppercase shadow-2xs">
                  <Award className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>Online Practice & Learning Tests</span>
                </div>
                
                <h1 className="!font-sans text-3xl sm:text-4xl lg:text-[44px] font-black text-[#131b2e] tracking-tight leading-[1.15]">
                  Online Mock Tests for Indian Competitive Exams
                </h1>
                
                <p className="!font-sans text-base sm:text-lg text-slate-600 leading-relaxed font-normal max-w-2xl mx-auto">
                  Practice tests and sectional drills with targeted timings, score analytics, and step-by-step 4-method conceptual learning solutions.
                </p>

                {/* Quick High-Level Metrics Strip */}
                <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 pt-3 text-xs sm:text-sm text-slate-600 font-medium">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span><strong>{tests.length}</strong> Curated Tests</span>
                  </div>
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-[#c8933b] shrink-0" />
                    <span><strong>{freeTestsCount}</strong> Free Samples</span>
                  </div>
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#131b2e] shrink-0" />
                    <span>Timed Practice</span>
                  </div>
                </div>
              </div>

              {/* Search & Filter Bar */}
              <div className="mt-8 max-w-4xl mx-auto space-y-4">
                <div className="relative w-full">
                  <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by test name, subject, or exam (e.g. SBI Clerk, NISM, Full Mock)..."
                    className="!font-sans pl-12 pr-4 h-12 bg-[#f8fafc] border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl text-sm sm:text-base focus:border-[#131b2e] focus:ring-1 focus:ring-[#131b2e] shadow-2xs"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 font-semibold"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Exam Categories Filter Bar */}
                {examOptions.length > 1 && (
                  <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-0.5 scrollbar-none">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider shrink-0 flex items-center gap-1">
                      <Filter className="w-3.5 h-3.5 text-slate-400" /> Exam:
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedExam('all')}
                      className={`!font-sans px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        selectedExam === 'all'
                          ? 'bg-[#131b2e] text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      All Exams
                    </button>
                    {examOptions.map((exam) => (
                      <button
                        key={exam}
                        type="button"
                        onClick={() => setSelectedExam(exam)}
                        className={`!font-sans px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          selectedExam === exam
                            ? 'bg-[#131b2e] text-white shadow-xs'
                            : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {exam}
                      </button>
                    ))}
                  </div>
                )}

                {/* Test Type Pills */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-slate-100">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedType('all')}
                      className={`!font-sans px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                        selectedType === 'all'
                          ? 'bg-[#131b2e] text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      All Formats ({tests.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedType('full_mock')}
                      className={`!font-sans px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                        selectedType === 'full_mock'
                          ? 'bg-[#131b2e] text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Full-Length Mocks ({fullMockCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedType('sectional')}
                      className={`!font-sans px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                        selectedType === 'sectional'
                          ? 'bg-[#131b2e] text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      Sectional Timers
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedType('free')}
                      className={`!font-sans px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                        selectedType === 'free'
                          ? 'bg-emerald-700 text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-emerald-800 hover:border-emerald-300'
                      }`}
                    >
                      Free Samples ({freeTestsCount})
                    </button>
                  </div>

                  <span className="text-xs text-slate-500 font-medium ml-auto">
                    Showing <strong>{filteredTests.length}</strong> mock tests
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Test Cards Grid */}
          <section className="w-full py-10 sm:py-16 bg-[#fafbfc] flex-1">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {filteredTests.length === 0 ? (
                <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl p-8 sm:p-12 space-y-4 shadow-xs max-w-lg mx-auto">
                  <div className="w-14 h-14 rounded-2xl bg-[#f4f7fb] border border-slate-200 text-[#131b2e] flex items-center justify-center mx-auto">
                    <Search className="w-7 h-7 text-[#c8933b]" />
                  </div>
                  <h3 className="!font-sans text-xl font-bold text-[#131b2e]">No mock tests match your criteria</h3>
                  <p className="!font-sans text-sm text-slate-600">Try clearing the search query or changing the filter options.</p>
                  <Button
                    variant="outline"
                    onClick={() => { setSearchQuery(''); setSelectedExam('all'); setSelectedType('all'); }}
                    className="!font-sans font-bold border-slate-300 hover:bg-slate-50 text-[#131b2e] rounded-xl h-10 px-5 text-xs cursor-pointer"
                  >
                    Reset All Filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                  {filteredTests.map((test) => {
                    const isFullMock = test.test_type === 'full_mock'
                    return (
                      <div
                        key={test.id}
                        className="bg-white border border-slate-200/90 rounded-2xl p-5 sm:p-6 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-200 group"
                      >
                        {/* Top Header: Exam Tag & Pricing/Sample Pill */}
                        <div className="space-y-3.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="!font-sans text-[11px] font-bold text-slate-600 bg-[#f4f7fb] border border-slate-200/80 px-2.5 py-1 rounded-md tracking-wide uppercase truncate max-w-[200px]">
                              {test.exam_name}
                            </span>

                            {test.is_free ? (
                              <span className="!font-sans text-[11px] font-bold px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                                Free Mock
                              </span>
                            ) : (
                              <span className="!font-sans text-[11px] font-semibold px-2.5 py-0.5 rounded-md bg-[#faf7f2] text-[#9b6818] border border-[#e8d5b5] shrink-0">
                                Starting ₹9
                              </span>
                            )}
                          </div>

                          {/* Test Title & Description */}
                          <div className="space-y-1.5">
                            <h3 className="!font-sans text-lg font-bold text-[#131b2e] group-hover:text-[#c8933b] transition-colors leading-snug line-clamp-2" title={test.title}>
                              {test.title}
                            </h3>
                            <p className="!font-sans text-xs text-slate-500 leading-relaxed line-clamp-2">
                              {test.description || `Simulated exam paper with authentic syllabus weightage, timer fidelity, and step-by-step solutions.`}
                            </p>
                          </div>

                          {/* 3-Cell Clean Metric Bar */}
                          <div className="grid grid-cols-3 gap-1.5 py-2.5 px-3 rounded-xl bg-[#f8fafc] border border-slate-100 text-center">
                            <div className="space-y-0.5">
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Duration</span>
                              <span className="text-xs font-bold text-slate-800 flex items-center justify-center gap-1">
                                <Clock className="w-3 h-3 text-[#c8933b]" />
                                {test.duration_minutes}m
                              </span>
                            </div>
                            <div className="space-y-0.5 border-x border-slate-200/70">
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Questions</span>
                              <span className="text-xs font-bold text-slate-800 flex items-center justify-center gap-1">
                                <BookOpen className="w-3 h-3 text-[#131b2e]" />
                                {test.question_count || 50} Qs
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Marking</span>
                              <span className="text-xs font-bold text-slate-800 flex items-center justify-center gap-1">
                                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                                {test.negative_marking > 0 ? `-${test.negative_marking}` : '0 Pen'}
                              </span>
                            </div>
                          </div>

                          {/* Format Tag Badges */}
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            <span className="text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                              {isFullMock ? 'Full Mock' : test.test_type.replace('_', ' ')}
                            </span>
                            {test.is_sectional && (
                              <span className="text-[11px] font-medium text-[#131b2e] bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
                                Sectional Timer
                              </span>
                            )}
                            <span className="text-[11px] font-medium text-slate-500 capitalize bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                              {test.difficulty} Level
                            </span>
                          </div>
                        </div>

                        {/* Bottom Action Area */}
                        <div className="pt-5 mt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                          <Link
                            href="/exams"
                            className="!font-sans text-xs font-medium text-slate-500 hover:text-[#131b2e] transition-colors"
                          >
                            Exam Blueprint
                          </Link>

                          <Button
                            asChild
                            className="!font-sans bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold h-9 px-4 rounded-lg shadow-2xs text-xs transition-all cursor-pointer group-hover:bg-[#c8933b]"
                          >
                            <a href="/users/sign_up" className="flex items-center gap-1.5">
                              <span>Practice Test Now</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Pillars matching MissionPricingSection */}
          <section className="w-full py-16 sm:py-24 bg-white border-t border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-xs font-bold tracking-wide uppercase">
                  <Award className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>The PrepKiya Advantage</span>
                </div>
                <h2 className="!font-sans text-3xl sm:text-4xl font-black text-[#131b2e] tracking-tight">
                  Targeted Practice & Deep Learning for Every Aspirant
                </h2>
                <p className="!font-sans text-base text-slate-600 max-w-2xl mx-auto">
                  Built to strengthen your problem solving, speed, and accuracy with comprehensive step solutions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 shadow-2xs">
                      <Clock className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-base text-[#131b2e] mb-1.5">
                      Sectional Timers
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Practice time allocation strategies across sections to build pacing and discipline.
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 shadow-2xs">
                      <Layers className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-base text-[#131b2e] mb-1.5">
                      4-Method Solutions
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Deep conceptual derivation, 10s speed shortcuts, boundary sanity checks, and spatial visual intuition.
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 shadow-2xs">
                      <TrendingUp className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-base text-[#131b2e] mb-1.5">
                      Percentile Rank
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Accurate benchmarking against thousands of verified attempts across India.
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 shadow-2xs">
                      <Zap className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-base text-[#131b2e] mb-1.5">
                      Pay-Per-Test Access
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      No expensive recurring subscriptions. Practice what you need starting at just ₹9.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
